import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AppProviders } from "@/app/providers/AppProviders";
import { App } from "@/App";
import { captureReferralFromUrl } from "@/lib/referral-code";
// ORDER MATTERS: the library's precompiled stylesheet FIRST. Both files put
// their utilities in `@layer utilities`, so the later one wins a tie — and the
// library ships bare `.max-w-lg` (its DialogContent default). Imported second,
// it beat every `sm:max-w-*` the app passes, capping every dialog at 512px
// and widening the narrow ones (2026-09-26). The app's classes are the
// override, so the app's stylesheet comes last.
import "bizpandit-ui/style.css";
import "@/styles/globals.css";

// Read ?ref= off the URL before React mounts. The router can move a visitor
// before any form renders — RoleHomeRedirect sends signed-out users from "/"
// to /event-mode, and the guards redirect too — and each of those drops the
// query string. Capturing here means the code survives whatever follows.
captureReferralFromUrl();

// One-time scrub: earlier builds wrote the whole registration payload,
// including the plaintext password, to localStorage and never removed it.
// This clears it from any device that still carries it. Safe to delete this
// block after a couple of releases.
try {
  localStorage.removeItem("leadRegistrationPayload");
} catch {
  /* private browsing / storage disabled — ignore */
}

/**
 * Recover from a stale chunk after a deploy.
 *
 * Every route in this app is lazily imported, so the running page holds
 * hashed filenames from the build it was served by. A deploy replaces those
 * files, and the next navigation asks for a chunk that no longer exists:
 *
 *   Failed to fetch dynamically imported module: .../assets/index-Cv7k6BbJ.js
 *
 * The user sees it as "the app breaks whenever I click anything", and a hard
 * refresh fixes it — which is exactly what this does for them. Vite fires
 * `vite:preloadError` for precisely this case.
 *
 * GUARDED AGAINST A RELOAD LOOP. If the chunk is missing for some other
 * reason — a bad deploy, a CDN fault — reloading forever would replace a
 * broken page with a spinning one, which is worse. One reload per tab: if the
 * error survives it, the real message is shown instead.
 */
const RELOADED_KEY = "bp:chunk-reload";
window.addEventListener("vite:preloadError", (event) => {
  let alreadyTried = "1";
  try {
    alreadyTried = sessionStorage.getItem(RELOADED_KEY) ?? "";
    sessionStorage.setItem(RELOADED_KEY, "1");
  } catch {
    /* storage disabled — fall through and let the error surface */
  }
  if (!alreadyTried) {
    event.preventDefault();
    window.location.reload();
  }
});

/**
 * Reset the guard only after the app has been up a while.
 *
 * Clearing it at load time — which is what this did first — defeats the whole
 * point: reload, key cleared immediately, next failure reloads again, loop.
 * The delay means the guard is only released once the page has proved it can
 * run, so a genuinely broken deploy reloads exactly once and then shows the
 * error, while a normal session gets a fresh retry for the next deploy.
 */
setTimeout(() => {
  try {
    sessionStorage.removeItem(RELOADED_KEY);
  } catch {
    /* ignore */
  }
}, 10_000);

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found. Check index.html for <div id=\"root\" />.");
}

createRoot(rootElement).render(
  <StrictMode>
    <AppProviders>
      <App />
    </AppProviders>
  </StrictMode>,
);
